import * as React from "react";
import Box from "@mui/material/Box";
import ButtonBase from "@mui/material/ButtonBase";
import Typography from "@mui/material/Typography";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";

export const ACCOUNT_MENU_WIDTH = 220;

const getMenuTextColor = (theme) =>
  theme.palette.mode === "dark" ? theme.palette.text.primary : "rgb(30, 41, 59)";
const getMenuMutedColor = (theme) =>
  theme.palette.mode === "dark" ? theme.palette.text.secondary : "rgb(100, 116, 139)";
const getMenuHoverBg = (theme) =>
  theme.palette.mode === "dark" ? "rgba(148, 163, 184, 0.14)" : "rgba(0, 0, 0, 0.04)";
const getMenuUserBg = (theme) =>
  theme.palette.mode === "dark" ? "rgba(148, 163, 184, 0.12)" : "rgb(241, 245, 249)";
const getMenuAvatarBg = (theme) =>
  theme.palette.mode === "dark" ? "rgba(148, 163, 184, 0.18)" : "rgb(226, 232, 240)";

export const ACCOUNT_MENU_PAPER_SX = {
  backgroundColor: (theme) => theme.palette.background.paper,
  border: (theme) => `1px solid ${theme.palette.border.default}`,
  borderRadius: "4px",
  boxShadow:
    "0px 2px 4px -1px rgba(0, 0, 0, 0.2), 0px 4px 5px 0px rgba(0, 0, 0, 0.14), 0px 1px 10px 0px rgba(0, 0, 0, 0.12)",
  color: getMenuTextColor,
  overflow: "hidden",
  width: ACCOUNT_MENU_WIDTH,
};

export const ACCOUNT_MENU_LIST_SX = {
  p: 0,
};

export const ACCOUNT_MENU_DIVIDER_SX = {
  borderColor: (theme) => theme.palette.border.default,
  my: 1,
};

export const ACCOUNT_MENU_ITEM_SX = {
  alignItems: "center",
  color: getMenuTextColor,
  display: "flex",
  fontSize: 14,
  fontWeight: 400,
  gap: 0,
  height: 48,
  lineHeight: "48px",
  minHeight: 48,
  px: 2,
  py: 0,
  "& > [aria-hidden='true']": {
    color: getMenuMutedColor,
    mr: 1.5,
  },
  "&:hover": {
    backgroundColor: getMenuHoverBg,
  },
  "&.Mui-focusVisible": {
    backgroundColor: getMenuHoverBg,
  },
};

export function resolveAccountMenuIdentity({ accountState, user }) {
  const storeAccountId =
    accountState?.accountId ||
    accountState?.profile?.account_id ||
    accountState?.profile?.id ||
    "";
  const storeMatchesUser =
    !user?.accountId || !storeAccountId || storeAccountId === user.accountId;
  const storeName = storeMatchesUser && accountState?.profile
    ? accountState?.info?.find((item) => item.label === "Nome da conta")?.value
    : "";

  const name = storeName || user?.accountName || "123zap";

  return {
    initial: name.trim().charAt(0).toUpperCase() || "Z",
    logoUrl: (storeMatchesUser ? accountState?.logoUrl : "") || user?.accountLogoUrl || "",
    name,
  };
}

export function AccountMenuHeader({
  avatarUrl,
  avatarVersion,
  email,
  initial,
  onEditProfile,
  productName = "123ZAP",
  userName,
}) {
  return (
    <>
      <Box
        sx={{
          alignItems: "center",
          display: "flex",
          height: 37,
          justifyContent: "center",
          textAlign: "center",
        }}
      >
        <Typography
          sx={{
            color: getMenuTextColor,
            fontSize: 14,
            fontWeight: 400,
            lineHeight: "21px",
          }}
        >
          {productName}
        </Typography>
      </Box>
      <Box sx={{ borderTop: (theme) => `1px solid ${theme.palette.border.default}` }} />
      <Box
        sx={{
          alignItems: "center",
          bgcolor: getMenuUserBg,
          display: "flex",
          gap: 1.5,
          height: 56,
          px: 2,
          py: 0.5,
        }}
      >
        <Box
          aria-hidden
          sx={{
            alignItems: "center",
            bgcolor: getMenuAvatarBg,
            borderRadius: "50%",
            color: getMenuTextColor,
            display: "flex",
            flexShrink: 0,
            fontSize: 13,
            fontWeight: 500,
            height: 32,
            justifyContent: "center",
            overflow: "hidden",
            width: 32,
          }}
        >
          {avatarUrl ? (
            <Box
              key={avatarVersion}
              component="img"
              src={avatarUrl}
              alt=""
              sx={{ height: "100%", objectFit: "cover", width: "100%" }}
            />
          ) : (
            initial
          )}
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            sx={{
              color: getMenuTextColor,
              fontSize: 14,
              fontWeight: 600,
              lineHeight: "18px",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {userName || "Usuário"}
          </Typography>
          <Typography
            sx={{
              color: getMenuMutedColor,
              fontSize: 12,
              fontWeight: 400,
              lineHeight: "16px",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {email || "Sessão ativa"}
          </Typography>
        </Box>
        {onEditProfile ? (
          <ButtonBase
            type="button"
            aria-label="Editar meus dados"
            onClick={(event) => {
              event.stopPropagation();
              onEditProfile();
            }}
            sx={{
              alignItems: "center",
              borderRadius: "50%",
              color: getMenuMutedColor,
              display: "flex",
              flexShrink: 0,
              height: 32,
              justifyContent: "center",
              width: 32,
              "&:hover": {
                bgcolor: getMenuHoverBg,
                color: getMenuTextColor,
              },
              "&:focus-visible": {
                outline: (theme) => `2px solid ${theme.palette.border.focus}`,
                outlineOffset: 2,
              },
            }}
          >
            <EditOutlinedIcon sx={{ fontSize: 18 }} />
          </ButtonBase>
        ) : null}
      </Box>
      <Box sx={{ borderTop: (theme) => `1px solid ${theme.palette.border.default}` }} />
    </>
  );
}
